import type {
  CoachTeamMembership,
  OrgSetting,
  ProfileRole,
} from '@strength/zero-schema';

// Only the fields role-sync reads off the viewer rows. Picked from the generated
// row types so they stay in sync with schema changes automatically.
// `role` and `defaultCoachRole` are enum-typed columns exposed as `unknown` by
// Zero's ZeroCustomType — callers must coerce to string before use.
type ProfileRoleInput = Pick<ProfileRole, 'role'>;
type TeamMembershipInput = Pick<CoachTeamMembership, 'teamId'>;
type OrgDefaultRoleInput = Pick<OrgSetting, 'defaultCoachRole'>;

// True once ALL three viewer queries have synced. Requiring all of them avoids
// seeding the fingerprint from partially-loaded data — otherwise it would shift
// as the remaining queries finish and trigger a spurious session refresh.
export function isRoleSyncComplete(
  profileRoleStatus: string,
  teamMembershipsStatus: string,
  orgDefaultRoleStatus: string,
): boolean {
  return (
    profileRoleStatus === 'complete' &&
    teamMembershipsStatus === 'complete' &&
    orgDefaultRoleStatus === 'complete'
  );
}

// Order-independent fingerprint of the viewer's role inputs; a change between
// definitive reads means effective access may have changed server-side.
export function viewerFingerprint(
  profileRoleRow: ProfileRoleInput | undefined,
  teamMembershipRows: TeamMembershipInput[],
  orgDefaultRoleRow?: OrgDefaultRoleInput | undefined,
): string {
  // Enum columns are `unknown` from Zero — coerce to string defensively.
  const role = String(profileRoleRow?.role ?? '');
  const teams = [...new Set(teamMembershipRows.map((m) => m.teamId ?? '<all>'))]
    .sort()
    .join(',');
  const orgDefault = String(orgDefaultRoleRow?.defaultCoachRole ?? '');
  return `${role}|${teams}|${orgDefault}`;
}

// Refresh only when the fingerprint changed from a previously-seen one. The
// first read (lastSeen === null) just seeds it — avoids a refresh loop on the
// post-rebuild re-sync. The caller gates on the sync being complete.
export function shouldRefreshSession(
  currentFingerprint: string,
  lastSeenFingerprint: string | null,
): boolean {
  if (lastSeenFingerprint === null) return false;
  return currentFingerprint !== lastSeenFingerprint;
}
